import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees } from '@/api/generated/company-management-user/company-management-user'
import type { EmployeeUserResponse, ServiceModuleModel } from '@/api/model'
import { PermissionEntityType } from '@/api/model'
import { usePermissions } from '@/hooks/usePermissions'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { Button } from '@/components/shadcn/button'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { FilterDateRangePicker } from '@/components/common/filters/FilterDateRangePicker'
import { useAbsenceEntries } from '../hooks/useAbsenceEntries'
import { AbsenceEntriesTable } from './AbsenceEntriesTable'

interface Props {
  module: ServiceModuleModel
  /** Fixed employee of this view (from the URL, `?view=employee&employeeId=…`). */
  employeeId: string
  onBack: () => void
}

/** Unwrap the query response from the axios wrapper if needed (hooks return the full response). */
function unwrap<T>(data: unknown): T | undefined {
  return ((data as { data?: T })?.data ?? data) as T | undefined
}

/**
 * Employee absences page (row click in the module overview → here).
 * Like `AbsenceAllEntriesPage`, but the employee is fixed (no employee filter)
 * and **without** a calendar. Only a date-range filter + paginated list for the
 * employee. The employee comes as an id from the URL (shareable / reload-safe) —
 * the name is looked up from the (cached) employee list of the module.
 */
export function AbsenceEmployeeEntriesPage({ module, employeeId, onBack }: Props) {
  const { t } = useTranslation(['service-modules-absence', 'common', 'filters'])
  const { userHasEntityPermission } = usePermissions()
  const [range, setRange] = useState<{ from?: string; to?: string }>({})
  // Approve/reject depends on ServiceModules.Edit of this module (global or object-specific).
  const canEdit = userHasEntityPermission(PermissionEntityType.ServiceModules, 'Edit', module.id)

  const employeesQuery = useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees(module.id)
  const employee = useMemo(
    () => (unwrap<EmployeeUserResponse[]>(employeesQuery.data) ?? []).find((e) => e.id === employeeId),
    [employeesQuery.data, employeeId],
  )

  const entries = useAbsenceEntries(module.id, {
    employeeUserId: employeeId,
    from: range.from,
    to: range.to,
  })

  const name = `${employee?.firstName ?? ''} ${employee?.lastName ?? ''}`.trim() || t('dropdown.employeeFallback', { ns: 'filters' })

  return (
    <PageLayout>
      <PageHeader title={t('absence.employeeTitle', { name })} description={t('absence.employeeDescription')}>
        <PageHeaderActions>
          <Button variant="outline" className="h-9 gap-2" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            {t('buttons.back', { ns: 'common' })}
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <FilterRow>
        <FilterDateRangePicker from={range.from} to={range.to} onChange={setRange} />
      </FilterRow>

      <AbsenceEntriesTable entries={entries} canEdit={canEdit} />
    </PageLayout>
  )
}
